import React, { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useTheme } from '../../contexts/ThemeContext.jsx';
import { useToast } from '../../contexts/ToastContext.jsx';
import ApiClient from '../../utils/apiClient.js';

/**
 * NotificationForm component for creating and editing notifications
 * Implements theme-aware styling and form validation
 */
const NotificationForm = ({
  notification = null,
  onClose,
  onSuccess
}) => {
  const { getToken } = useAuth();
  const { theme } = useTheme();
  const { showToast } = useToast();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    body: '',
    is_active: true
  });
  const [errors, setErrors] = useState({});
  const [touched, setTouched] = useState({});

  // Initialize API client
  const apiClient = new ApiClient(getToken);

  // Initialize form data when editing
  useEffect(() => {
    if (notification) {
      setFormData({
        title: notification.title || '',
        body: notification.body || '',
        is_active: notification.is_active !== undefined ? notification.is_active : true
      });
    }
  }, [notification]);

  /**
   * Validate form data with enhanced validation rules
   */
  const validateForm = () => {
    const newErrors = {};

    // Title validation
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    } else if (formData.title.trim().length < 1) {
      newErrors.title = 'Title cannot be empty';
    } else if (formData.title.length > 255) {
      newErrors.title = 'Title must be 255 characters or less';
    } else if (formData.title.trim() !== formData.title) {
      newErrors.title = 'Title cannot start or end with whitespace';
    }

    // Body validation
    if (!formData.body.trim()) {
      newErrors.body = 'Body is required';
    } else if (formData.body.trim().length < 1) {
      newErrors.body = 'Body cannot be empty';
    } else if (formData.body.length > 10000) {
      newErrors.body = 'Body must be 10000 characters or less';
    }

    // Check for potentially dangerous content
    const dangerousPatterns = [
      /<script[^>]*>.*?<\/script>/gi,
      /<iframe[^>]*>.*?<\/iframe>/gi,
      /javascript:/gi,
      /on\w+\s*=/gi
    ];

    dangerousPatterns.forEach(pattern => {
      if (pattern.test(formData.title) || pattern.test(formData.body)) {
        newErrors.security = 'Content contains potentially unsafe elements';
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  /**
   * Handle input change
   */
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }));
    }

    // Mark field as touched
    setTouched(prev => ({
      ...prev,
      [field]: true
    }));
  };

  /**
   * Handle form submission with enhanced error handling
   */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      showToast('Please fix the form errors before submitting', 'error');
      return;
    }

    setLoading(true);

    try {
      const submitData = {
        title: formData.title.trim(),
        body: formData.body.trim(),
        is_active: formData.is_active
      };

      let response;
      if (notification) {
        // Update existing notification
        response = await apiClient.put(`/api/admin/notifications/${notification.id}`, submitData);
      } else {
        // Create new notification
        response = await apiClient.post('/api/admin/notifications', submitData);
      }

      if (response.success) {
        showToast(
          notification 
            ? 'Notification updated successfully' 
            : 'Notification created successfully', 
          'success'
        );
        onSuccess();
        onClose();
      } else {
        // Handle structured error responses from backend
        if (response.error && response.error.code === 'VALIDATION_ERROR') {
          const validationErrors = response.error.details?.validation_errors || {};
          setErrors(validationErrors);
          showToast('Please fix the validation errors and try again', 'error');
        } else if (response.error && response.error.code === 'AUTHORIZATION_ERROR') {
          showToast('Access denied. Admin permission required.', 'error');
        } else if (response.error && response.error.code === 'CONFLICT_ERROR') {
          showToast('A conflict occurred. Please refresh and try again.', 'error');
        } else {
          throw new Error(response.error?.message || response.message || 'Failed to save notification');
        }
      }
    } catch (error) {
      console.error('Failed to save notification:', error);
      
      // Handle different types of errors
      if (error.name === 'TypeError' && error.message.includes('fetch')) {
        showToast('Network error. Please check your connection and try again.', 'error');
      } else if (error.message.includes('401') || error.message.includes('unauthorized')) {
        showToast('Session expired. Please refresh the page and try again.', 'error');
      } else if (error.message.includes('403') || error.message.includes('forbidden')) {
        showToast('Access denied. Admin permission required.', 'error');
      } else if (error.message.includes('422')) {
        showToast('Invalid data provided. Please check your input and try again.', 'error');
      } else if (error.message.includes('500')) {
        showToast('Server error. Please try again later.', 'error');
      } else {
        showToast(error.message || 'Failed to save notification. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  /**
   * Handle cancel
   */
  const handleCancel = () => {
    onClose();
  };

  /**
   * Get field error styling
   */
  const getFieldErrorStyling = (field) => {
    if (errors[field] && touched[field]) {
      return theme === 'dark'
        ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
        : 'border-red-300 focus:border-red-500 focus:ring-red-500';
    }
    return theme === 'dark'
      ? 'border-gray-600 focus:border-teal-500 focus:ring-teal-500'
      : 'border-gray-300 focus:border-teal-500 focus:ring-teal-500';
  };

  return (
    <div className={`${
      theme === 'dark' 
        ? 'bg-gray-800 border-gray-700' 
        : 'bg-white border-gray-200'
    } rounded-lg border shadow-lg`}>
      {/* Form Header */}
      <div className={`px-6 py-4 ${
        theme === 'dark' ? 'bg-gray-900 border-gray-700' : 'bg-gray-50 border-gray-200'
      } border-b rounded-t-lg`}>
        <div className="flex justify-between items-center">
          <h3 className={`text-lg font-medium ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            {notification ? 'Edit Notification' : 'Create New Notification'}
          </h3>
          <button
            onClick={handleCancel}
            className={`text-gray-400 hover:text-gray-600 ${
              theme === 'dark' ? 'hover:text-gray-200' : ''
            } transition-colors`}
            disabled={loading}
          >
            ✕
          </button>
        </div>
      </div>

      {/* Form Content */}
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Security Error Display */}
        {errors.security && (
          <div className={`p-4 rounded-lg border ${
            theme === 'dark' 
              ? 'bg-red-900/20 border-red-500/30 text-red-400' 
              : 'bg-red-50 border-red-200 text-red-800'
          }`}>
            <div className="flex items-center gap-2">
              <span className="text-lg">⚠️</span>
              <span className="font-medium">Security Warning</span>
            </div>
            <p className="mt-1 text-sm">{errors.security}</p>
          </div>
        )}

        {/* Title Field */}
        <div>
          <label 
            htmlFor="notification-title"
            className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}
          >
            Title *
          </label>
          <input
            id="notification-title"
            type="text"
            value={formData.title}
            onChange={(e) => handleInputChange('title', e.target.value)}
            onBlur={() => setTouched(prev => ({ ...prev, title: true }))}
            placeholder="Enter notification title..."
            className={`w-full px-4 py-2 rounded-lg border transition-colors focus:ring-2 ${
              theme === 'dark'
                ? `bg-gray-700 text-white placeholder-gray-300 ${getFieldErrorStyling('title')}`
                : `bg-white text-gray-900 placeholder-gray-500 ${getFieldErrorStyling('title')}`
            }`}
            disabled={loading}
            maxLength={255}
          />
          {errors.title && touched.title && (
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            }`}>
              {errors.title}
            </p>
          )}
          <p className={`mt-1 text-xs ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {formData.title.length}/255 characters
          </p>
        </div>

        {/* Body Field */}
        <div>
          <label 
            htmlFor="notification-body"
            className={`block text-sm font-medium ${
              theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
            } mb-2`}
          >
            Body *
          </label>
          <textarea
            id="notification-body"
            value={formData.body}
            onChange={(e) => handleInputChange('body', e.target.value)}
            onBlur={() => setTouched(prev => ({ ...prev, body: true }))}
            placeholder="Enter notification body content..."
            rows={8}
            className={`w-full px-4 py-2 rounded-lg border transition-colors focus:ring-2 resize-vertical ${
              theme === 'dark'
                ? `bg-gray-700 text-white placeholder-gray-300 ${getFieldErrorStyling('body')}`
                : `bg-white text-gray-900 placeholder-gray-500 ${getFieldErrorStyling('body')}`
            }`}
            disabled={loading}
            maxLength={10000}
          />
          {errors.body && touched.body && (
            <p className={`mt-1 text-sm ${
              theme === 'dark' ? 'text-red-400' : 'text-red-600'
            }`}>
              {errors.body}
            </p>
          )}
          <p className={`mt-1 text-xs ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {formData.body.length}/10000 characters
          </p>
        </div>

        {/* Active Status Field */}
        <div>
          <label className={`block text-sm font-medium ${
            theme === 'dark' ? 'text-gray-200' : 'text-gray-700'
          } mb-2`}>
            Status
          </label>
          <div className="flex items-center space-x-4">
            <label className="flex items-center">
              <input
                type="radio"
                name="is_active"
                checked={formData.is_active === true}
                onChange={() => handleInputChange('is_active', true)}
                className="mr-2 text-teal-600 focus:ring-teal-500"
                disabled={loading}
              />
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Active
              </span>
            </label>
            <label className="flex items-center">
              <input
                type="radio"
                name="is_active"
                checked={formData.is_active === false}
                onChange={() => handleInputChange('is_active', false)}
                className="mr-2 text-teal-600 focus:ring-teal-500"
                disabled={loading}
              />
              <span className={`text-sm ${
                theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
              }`}>
                Inactive (Draft)
              </span>
            </label>
          </div>
          <p className={`mt-1 text-xs ${
            theme === 'dark' ? 'text-gray-400' : 'text-gray-500'
          }`}>
            {formData.is_active 
              ? 'Active notifications are visible to users' 
              : 'Inactive notifications are saved as drafts and not visible to users'
            }
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-4 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleCancel}
            className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
              theme === 'dark'
                ? 'bg-gray-700 text-gray-300 hover:bg-gray-600 hover:text-white'
                : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:text-gray-900'
            }`}
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className={`px-6 py-2 text-sm font-medium text-white rounded-lg transition-colors focus:ring-2 focus:ring-teal-500 focus:ring-offset-2 ${
              loading
                ? 'bg-gray-400 cursor-not-allowed'
                : 'bg-teal-600 hover:bg-teal-700'
            }`}
            disabled={loading}
          >
            {loading ? (
              <div className="flex items-center gap-2">
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                {notification ? 'Updating...' : 'Creating...'}
              </div>
            ) : (
              notification ? 'Update Notification' : 'Create Notification'
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NotificationForm;
